// src/services/employeeAccessApi.js
import { apiClient } from '../utils/apiClient';

// ============== MEVCUTLAR ==============
export const getDevices = async () => {
    const list = await apiClient('Device');
    if (!Array.isArray(list)) return [];
    return list.map(d => ({
        id: d.id,
        deviceName: d.deviceName || d.name || `${d.brand || ''} ${d.model || ''}`.trim(),
        brand: d.brand,
        model: d.model,
        ipAddress: d.ipAddress,
        port: d.port,
        sdkPort: d.sdkPort,
    }));
};

export const getCredentials = (employeeId) =>
    apiClient(`Employee/${employeeId}/credentials`);

export const getPermissions = (employeeId) =>
    apiClient(`Employee/${employeeId}/device-permissions`);

export const startCardCapture = (employeeId, deviceId, timeoutSeconds = 90) =>
    apiClient(`Employee/${employeeId}/enroll/card/start`, {
        method: 'POST',
        body: { deviceId, timeoutSeconds },
    });

export const pollCardCapture = (operationId) =>
    apiClient(`Employee/enroll/card/${encodeURIComponent(operationId)}`);

export const cancelLive = (ipAddress, port) =>
    apiClient('Employee/live/cancel', {
        method: 'POST',
        body: { ipAddress, port },
    });

/**
 * Kartı cihaza yaz (ve opsiyonel DB’ye kaydet).
 * options: { takeover?: boolean, replace?: boolean }
 * backend query: ?takeover=true | ?replace=true
 */
export const pushCard = (employeeId, deviceId, cardNo, saveToDb = true, options = {}) =>
    apiClient(`Employee/${employeeId}/push/card`, {
        method: 'POST',
        body: { deviceId, cardNo, saveCredential: saveToDb },
        queryParams: {
            takeover: options.takeover ? 'true' : undefined,
            replace: options.replace ? 'true' : undefined,
        },
    });

/**
 * Sadece DB’ye kimlik ekle.
 * options: { takeover?: boolean, replace?: boolean }
 */
export const addCredential = (employeeId, payload, options = {}) =>
    apiClient(`Employee/${employeeId}/credentials`, {
        method: 'POST',
        body: payload,
        queryParams: {
            takeover: options.takeover ? 'true' : undefined,
            replace: options.replace ? 'true' : undefined,
        },
    });

export const generateQrCard = (employeeId) =>
    apiClient(`Employee/${employeeId}/credentials/generate-qr`, {
        method: 'POST'
    });

export const generateNfcCard = (employeeId) =>
    apiClient(`Employee/${employeeId}/credentials/generate-nfc`, {
        method: 'POST'
    });

export const convertCardNumber = (decimalValue) =>
    apiClient(`Util/convert-card-number?decimalValue=${encodeURIComponent(decimalValue)}`);

// ============== YENİ EKLENENLER (YETKİ GRUPLARI) ==============

/**
 * Grup seçimi için dropdown verisi.
 * Önce /access-groups/dropdown dener; yoksa /access-groups?page=1&pageSize=1000
 * dönüşünü { id, name } listesine indirger.
 */
export const listAccessGroupsForDropdown = async () => {
    try {
        // Eğer backend'de özel dropdown endpoint'in varsa önce onu kullan
        const dd = await apiClient('access-groups/dropdown');
        if (Array.isArray(dd)) {
            return dd.map(g => ({ id: g.id || g.Id || g.value || g.Value, name: g.name || g.Name || g.label || g.Label }));
        }
    } catch {
        // yoksa fallback'e düş
    }

    // fallback: sayfalı listeyi çek, items -> dropdown'a indir
    const paged = await apiClient('access-groups', { method: 'GET', queryParams: { page: 1, pageSize: 1000 } });
    const items = Array.isArray(paged?.items) ? paged.items : (Array.isArray(paged) ? paged : []);
    return items.map(g => ({ id: g.id || g.Id, name: g.name || g.Name }));
};

/** Personelin üye olduğu gruplar (liste) */
export const listEmployeeGroups = (employeeId) =>
    apiClient(`employees/${employeeId}/access-groups`, { method: 'GET' });

/**
 * Personeli birden fazla gruba üye yap.
 * opts: { isFullDay?: boolean, validFrom?: string, validTo?: string }
 */
export const assignEmployeeGroups = (employeeId, groupIds, opts = {}) =>
    apiClient(`employees/${employeeId}/access-groups`, {
        method: 'POST',
        body: {
            groupIds,
            isFullDay: opts.isFullDay ?? true,
            validFrom: opts.validFrom ?? null,
            validTo: opts.validTo ?? null
            // Status alanı DTO’da yok; backend varsayılan "Active" veriyor.
        }
    });

/** Üyelik sil (soft revoke) */
export const revokeEmployeeMembership = (employeeId, membershipId) =>
    apiClient(`employees/${employeeId}/access-groups/${membershipId}`, {
        method: 'DELETE'
    });


/**
 * Tek personele push: Seçili (veya tüm aktif) gruplar için job üretir.
 * operation: "Grant" | "Update" | "Revoke"
 */
export const updateMembership = (employeeId, membershipId, payload = {}) =>
    apiClient(`employees/${employeeId}/access-groups/${membershipId}`, {
        method: 'PUT',
        body: payload // boş {} da gönderebilirsin; Update job oluşur
    });


// Toplu export
export const employeeAccessApi = {
    getDevices,
    getCredentials,
    getPermissions,
    startCardCapture,
    pollCardCapture,
    cancelLive,
    pushCard,
    addCredential,
    generateQrCard,
    generateNfcCard,
    convertCardNumber,

    // yeni eklenenler
    listAccessGroupsForDropdown,
    listEmployeeGroups,
    assignEmployeeGroups,
    revokeEmployeeMembership,
    updateMembership,

    // PARMAK İZİ
    startFingerCapture: (employeeId, deviceId, fingerIndex) =>
        apiClient(`Employee/${employeeId}/enroll/fingerprint/start`, {
            method: 'POST', body: { deviceId, fingerIndex }
        }),

    pollFingerCapture: (operationId) =>
        apiClient(`Employee/enroll/fingerprint/${encodeURIComponent(operationId)}`),

    saveFingerprint: (employeeId, payload) =>
        apiClient(`Employee/${employeeId}/fingerprints`, { method: 'POST', body: payload }),

    getFingerprints: (employeeId) =>
        apiClient(`Employee/${employeeId}/fingerprints`),

    deleteFingerprint: (employeeId, fingerIndex) =>
        apiClient(`Employee/${employeeId}/fingerprints/${fingerIndex}`, { method: 'DELETE' }),

    updatePriority: (employeeId, priorityMode) =>
        apiClient(`Employee/${employeeId}/priority`, {
            method: 'PUT',
            body: { priorityMode }
        }),

    deleteCredential: (employeeId, credentialId) =>
        apiClient(`Employee/${employeeId}/credentials/${credentialId}`, {
            method: 'DELETE'
        })
};
